import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { dashboardCompanyOverviewUrl } from 'dashboard409a/common/config/urls';
import { MODEL_SUBFOLDER, REPORT_SUBFOLDER } from 'dashboard409a/components/OperationsModal/dropbox.constants';
import { buildDropboxPath } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import useDropbox from 'dashboard409a/components/OperationsModal/useDropbox';
import {
  draftFinalReportEmail,
  findScalarOpinion,
  getLatestValuationExtractionRun,
  getReportContacts,
  getReportSelection,
  getValuationExtractionStatus,
  saveReportSelection,
  startValuationExtraction,
  updateScalarOpinion,
} from 'dashboard409a/services/dashboard';
import { Logger } from 'utilities';
import { ProcessReportModalProps } from './ProcessReportModal.props';
import { FileCheckboxItem } from './ProcessReportModal.types';
import {
  contactSelectKey,
  deriveDatePrefix,
  fileNameFromPath,
  formatCombinedEmail,
  isTerminalExtractionStatus,
} from './ProcessReportModal.utils';

type UseProcessReportModalParams = Pick<
  ProcessReportModalProps,
  'open' | 'cmdId' | 'valuationId' | 'valuationFolderPath' | 'existingReportReference'
>;

// Delay before persisting email edits so we save once the analyst pauses typing.
const DRAFT_EMAIL_SAVE_DEBOUNCE_MS = 800;

const useProcessReportModal = ({
  open,
  cmdId,
  valuationId,
  valuationFolderPath,
  existingReportReference,
}: UseProcessReportModalParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { listFiles } = useDropbox();

  const [selectedModelPath, setSelectedModelPath] = useState<string | null>(null);
  const [selectedReportPath, setSelectedReportPath] = useState<string | null>(
    existingReportReference ?? null
  );
  // Stable Select value: prefer Attio recordId, fall back to email (emails alone can be blank).
  const [selectedContactKey, setSelectedContactKey] = useState<string>('');
  // Combined subject + body for the design's single email surface.
  const [draftEmail, setDraftEmail] = useState('');
  const [draftDashboardUrl, setDraftDashboardUrl] = useState<string | null>(null);
  const [notesParsed, setNotesParsed] = useState<number | null>(null);
  const [initialPickerPrefix, setInitialPickerPrefix] = useState('');
  const [extractionRunId, setExtractionRunId] = useState<string | null>(null);
  // True once the analyst kicks off a run. The email is drafted immediately and
  // in parallel with the async extraction — we no longer wait for processing.
  const [processInitiated, setProcessInitiated] = useState(false);

  // Remember the model/report selections per valuation (server-persisted, keyed by Attio id).
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef<string>('');
  // Ensure we draft only once per initiated run (guards against re-fires when
  // the linked CMD/opinion ids settle after extraction starts).
  const draftInitiatedRef = useRef(false);
  // Populate the saved email from the opinion only once per open, and never
  // over a freshly drafted/edited email in the same session.
  const populatedSavedEmailRef = useRef(false);

  const { data: savedSelection, isFetched: isSelectionFetched } = useQuery({
    queryKey: ['valuation-report-selection', valuationId],
    queryFn: () => getReportSelection(valuationId as string),
    enabled: open && Boolean(valuationId),
  });

  const { data: latestExtractionRun } = useQuery({
    queryKey: ['latest-valuation-extraction-run', valuationId],
    queryFn: () => getLatestValuationExtractionRun(valuationId as string),
    enabled: open && Boolean(valuationId),
    // After extraction completes, CMD/opinion linkage can lag the import callback — keep polling.
    refetchInterval: query => {
      if (!open || !extractionRunId) {
        return false;
      }
      const run = query.state.data;
      const runLinked =
        run?.run_id === extractionRunId &&
        Boolean(run?.cmd_id) &&
        Boolean(run?.scalar_opinion_id);
      return runLinked ? false : 3000;
    },
  });

  const effectiveCmdId = cmdId ?? latestExtractionRun?.cmd_id ?? undefined;

  // The drafted email lives on the ScalarOpinion produced by the extraction run.
  // For standalone extractions that CMD can differ from the Attio-linked CMD used
  // for contacts/files, so prefer the extraction run's CMD for the email opinion
  // — otherwise a saved draft is written to and read from mismatched opinions.
  const opinionCmdId = latestExtractionRun?.cmd_id ?? cmdId ?? undefined;

  // Load the persisted opinion so a previously saved email can prefill the editor.
  const { data: savedOpinion } = useQuery({
    queryKey: ['scalar-opinion', opinionCmdId],
    queryFn: () => findScalarOpinion(opinionCmdId as number),
    enabled: open && Boolean(opinionCmdId),
  });

  // Prefill the editor with the saved email once per open. Skip once an
  // extraction has started this session so a fresh draft is never overwritten.
  useEffect(() => {
    if (
      !open ||
      populatedSavedEmailRef.current ||
      extractionRunId ||
      processInitiated ||
      draftEmail
    ) {
      return;
    }
    const saved = savedOpinion?.drafted_email;
    if (saved) {
      setDraftEmail(saved);
      populatedSavedEmailRef.current = true;
    }
  }, [open, savedOpinion?.drafted_email, extractionRunId, processInitiated, draftEmail]);

  // Persist the current email onto the opinion so it survives modal reopen.
  const { mutate: persistDraftedEmail } = useMutation({
    mutationFn: ({ persistCmdId, email }: { persistCmdId: number; email: string }) =>
      updateScalarOpinion(persistCmdId, { drafted_email: email }),
    onError: error => Logger.error('Failed to save drafted email:', error),
  });

  // Debounce writes while the analyst is typing so we save once they pause
  // instead of on every keystroke.
  const debouncedPersistDraftedEmail = useMemo(
    () =>
      debounce((persistCmdId: number, email: string) => {
        persistDraftedEmail({ persistCmdId, email });
      }, DRAFT_EMAIL_SAVE_DEBOUNCE_MS),
    [persistDraftedEmail]
  );

  // Cancel any pending debounced save on unmount to avoid a late write.
  useEffect(() => () => debouncedPersistDraftedEmail.cancel(), [debouncedPersistDraftedEmail]);

  // Edit handler: update the field and schedule a debounced save.
  const handleEmailChange = useCallback(
    (email: string) => {
      setDraftEmail(email);
      if (opinionCmdId) {
        debouncedPersistDraftedEmail(opinionCmdId, email);
      }
    },
    [opinionCmdId, debouncedPersistDraftedEmail]
  );

  // Flush immediately (on blur) so the last edit is not left pending.
  const saveDraftedEmail = useCallback(() => {
    if (!opinionCmdId) {
      return;
    }
    debouncedPersistDraftedEmail.cancel();
    persistDraftedEmail({ persistCmdId: opinionCmdId, email: draftEmail });
  }, [opinionCmdId, draftEmail, persistDraftedEmail, debouncedPersistDraftedEmail]);

  const extractionDashboardUrl = useMemo(() => {
    if (!latestExtractionRun?.company_slug) {
      return null;
    }
    return dashboardCompanyOverviewUrl(latestExtractionRun.company_slug);
  }, [latestExtractionRun?.company_slug]);

  // Prefer the URL returned with the email draft (deterministic from CMD) over the
  // extraction-run link, which is only available after import completes.
  const dashboardOpinionUrl = draftDashboardUrl || extractionDashboardUrl;

  const { mutate: persistSelection } = useMutation({
    mutationFn: (payload: { model_reference: string | null; report_reference: string | null }) =>
      saveReportSelection(valuationId as string, payload),
    onError: error => Logger.error('Failed to save report selection:', error),
  });

  // When no Dropbox valuation folder is linked, fall back to searching the
  // Dropbox backup S3 bucket via typeahead instead of listing folder contents.
  const useS3Fallback = !valuationFolderPath;

  const modelFolderPath = useMemo(
    () => (valuationFolderPath ? buildDropboxPath(valuationFolderPath, MODEL_SUBFOLDER) : null),
    [valuationFolderPath]
  );
  const reportFolderPath = useMemo(
    () => (valuationFolderPath ? buildDropboxPath(valuationFolderPath, REPORT_SUBFOLDER) : null),
    [valuationFolderPath]
  );

  const { data: modelFiles = [], isLoading: isLoadingModelFiles } = useQuery({
    queryKey: ['process-report-model-files', modelFolderPath],
    queryFn: () => listFiles(modelFolderPath as string),
    enabled: open && Boolean(modelFolderPath),
  });

  const { data: reportFiles = [], isLoading: isLoadingReportFiles } = useQuery({
    queryKey: ['process-report-report-files', reportFolderPath],
    queryFn: () => listFiles(reportFolderPath as string),
    enabled: open && Boolean(reportFolderPath),
  });

  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ['process-report-contacts', effectiveCmdId],
    queryFn: () => getReportContacts(effectiveCmdId as number),
    enabled: open && Boolean(effectiveCmdId),
  });

  const modelCheckboxFiles = useMemo<FileCheckboxItem[]>(
    () =>
      modelFiles.map(file => ({
        id: file.pathDisplay || file.name,
        name: file.name,
        path: file.pathDisplay,
      })),
    [modelFiles]
  );
  const reportCheckboxFiles = useMemo<FileCheckboxItem[]>(
    () =>
      reportFiles.map(file => ({
        id: file.pathDisplay || file.name,
        name: file.name,
        path: file.pathDisplay,
      })),
    [reportFiles]
  );

  // Reset transient draft state when the modal is reopened.
  useEffect(() => {
    if (open) {
      setSelectedContactKey('');
      setDraftEmail('');
      setDraftDashboardUrl(null);
      setNotesParsed(null);
      setExtractionRunId(null);
      setProcessInitiated(false);
      draftInitiatedRef.current = false;
      populatedSavedEmailRef.current = false;
    }
  }, [open]);

  // Hydrate remembered selections (once per open) and reopen the picker at the
  // previously navigated folder.
  useEffect(() => {
    if (!open) {
      hydratedRef.current = false;
      return;
    }
    if (isSelectionFetched && !hydratedRef.current) {
      const modelKey = savedSelection?.model_reference ?? null;
      const reportKey = savedSelection?.report_reference ?? existingReportReference ?? null;
      setSelectedModelPath(modelKey);
      setSelectedReportPath(reportKey);
      setInitialPickerPrefix(deriveDatePrefix(reportKey ?? modelKey));
      lastSavedRef.current = JSON.stringify({ modelKey, reportKey });
      hydratedRef.current = true;
    }
  }, [open, isSelectionFetched, savedSelection, existingReportReference]);

  // Persist selections to the server whenever they change after hydration.
  useEffect(() => {
    if (!open || !hydratedRef.current || !valuationId) {
      return;
    }
    const serialized = JSON.stringify({
      modelKey: selectedModelPath,
      reportKey: selectedReportPath,
    });
    if (serialized !== lastSavedRef.current) {
      lastSavedRef.current = serialized;
      persistSelection({
        model_reference: selectedModelPath,
        report_reference: selectedReportPath,
      });
    }
  }, [open, valuationId, selectedModelPath, selectedReportPath, persistSelection]);

  const selectedModelName = fileNameFromPath(selectedModelPath);
  const selectedReportName = fileNameFromPath(selectedReportPath);

  const selectedContact = useMemo(
    () =>
      contacts.find((contact, index) => contactSelectKey(contact, index) === selectedContactKey) ??
      null,
    [contacts, selectedContactKey]
  );

  // Keep selection valid when contacts reload; auto-pick the first contact once available.
  useEffect(() => {
    if (!open || contacts.length === 0) {
      return;
    }
    const keys = contacts.map((contact, index) => contactSelectKey(contact, index));
    if (selectedContactKey && keys.includes(selectedContactKey)) {
      return;
    }
    setSelectedContactKey(keys[0] ?? '');
  }, [open, contacts, selectedContactKey]);

  const persistReportReference = useCallback(
    async (reportCmdId: number) => {
      if (!selectedReportPath || selectedReportPath === existingReportReference) {
        return;
      }
      try {
        await updateScalarOpinion(reportCmdId, { final_report_pdf_reference: selectedReportPath });
      } catch (error) {
        Logger.error('Failed to persist final report reference:', error);
      }
    },
    [selectedReportPath, existingReportReference]
  );

  const { mutate: runDraft, isPending: isDrafting } = useMutation({
    mutationFn: async (draftCmdId: number) => {
      await persistReportReference(draftCmdId);
      return draftFinalReportEmail(draftCmdId, {
        model_filename: selectedModelName,
        report_filename: selectedReportName,
        contact_name: selectedContact?.name,
        contact_email: selectedContact?.email,
      });
    },
    onSuccess: (result, draftCmdId) => {
      const combinedEmail = formatCombinedEmail(result.subject, result.body);
      setDraftEmail(combinedEmail);
      setDraftDashboardUrl(result.dashboard_url || null);
      setNotesParsed(result.notes_parsed);
      // Persist the combined subject+body so it matches the editor on reopen.
      persistDraftedEmail({ persistCmdId: draftCmdId, email: combinedEmail });
    },
    onError: error => {
      Logger.error('Failed to draft final report email:', error);
      enqueueSnackbar('Failed to draft final report email. Please try again.', { variant: 'error' });
      // Allow a retry for the same run after a draft failure.
      draftInitiatedRef.current = false;
    },
  });

  const { mutate: runExtraction, isPending: isStartingExtraction } = useMutation({
    mutationFn: async () => {
      if (!valuationId) {
        throw new Error('Missing valuation');
      }
      // Persist the latest selection so the backend reads the current refs.
      await saveReportSelection(valuationId, {
        model_reference: selectedModelPath,
        report_reference: selectedReportPath,
      });
      return startValuationExtraction(valuationId);
    },
    onSuccess: result => {
      const runId = result.run_id ?? result.exec_id ?? null;
      setExtractionRunId(runId);
      // Note: the draft runs in parallel (see runProcess) and must not be
      // cleared here, otherwise the just-drafted email would be wiped when the
      // extraction-start response resolves.
      if (valuationId) {
        queryClient.invalidateQueries({ queryKey: ['latest-valuation-extraction-run', valuationId] });
        // ensure_standalone_company may have just created the CMD for this valuation.
        queryClient.invalidateQueries({
          queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
        });
      }
    },
    onError: (error: Error) => {
      Logger.error('Failed to start valuation extraction:', error);
      enqueueSnackbar(error.message || 'Failed to start valuation extraction. Please try again.', {
        variant: 'error',
      });
    },
  });

  const {
    data: extractionStatus,
    isError: isExtractionStatusError,
    error: extractionStatusError,
  } = useQuery({
    queryKey: ['valuation-extraction-status', extractionRunId],
    queryFn: () => getValuationExtractionStatus(extractionRunId as string),
    enabled: open && Boolean(extractionRunId),
    refetchInterval: query =>
      isTerminalExtractionStatus(query.state.data?.status) ? false : 5000,
    retry: false,
  });

  useEffect(() => {
    if (
      open &&
      valuationId &&
      isTerminalExtractionStatus(extractionStatus?.status) &&
      extractionStatus?.status?.toLowerCase() === 'completed'
    ) {
      queryClient.invalidateQueries({ queryKey: ['latest-valuation-extraction-run', valuationId] });
      queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
    }
  }, [open, valuationId, extractionStatus?.status, queryClient]);

  const isExtractionProcessing =
    Boolean(extractionRunId) &&
    !isExtractionStatusError &&
    !isTerminalExtractionStatus(extractionStatus?.status);

  const isExtractionFailed =
    Boolean(extractionRunId) && extractionStatus?.status?.toLowerCase() === 'failed';

  // Draft the email as soon as an opinion CMD is available — the dashboard URL
  // is deterministic (company slug), so we do not wait for the async extraction
  // to finish. For a standalone valuation the CMD is created synchronously when
  // extraction starts, so `opinionCmdId` settles shortly after `runProcess`.
  useEffect(() => {
    if (!open || !processInitiated || isDrafting || draftInitiatedRef.current) {
      return;
    }
    // An email already exists (freshly drafted or a saved draft): keep it and
    // skip the LLM draft — extraction still runs. Latch so we don't redraft if
    // the analyst later edits/clears the field mid-run.
    if (draftEmail) {
      draftInitiatedRef.current = true;
      return;
    }
    if (!opinionCmdId) {
      return;
    }
    draftInitiatedRef.current = true;
    runDraft(opinionCmdId);
  }, [open, processInitiated, opinionCmdId, isDrafting, draftEmail, runDraft]);

  // Kick off extraction and drafting together so the analyst never waits on the
  // (async) valuation processing to see the drafted email. Any existing email is
  // preserved — the draft effect above only drafts when the field is empty.
  const runProcess = useCallback(() => {
    draftInitiatedRef.current = false;
    setProcessInitiated(true);
    runExtraction();
  }, [runExtraction]);

  const isPipelineBusy = isStartingExtraction || isExtractionProcessing || isDrafting;

  const canProcess =
    Boolean(valuationId) &&
    Boolean(selectedModelPath) &&
    Boolean(selectedReportPath) &&
    !isPipelineBusy;

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(draftEmail);
      enqueueSnackbar('Email copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy email:', error);
      enqueueSnackbar('Failed to copy email to clipboard.', { variant: 'error' });
    }
  }, [draftEmail, enqueueSnackbar]);

  let processButtonLabel = 'Draft Final Report Email';
  if (isStartingExtraction) {
    processButtonLabel = 'Starting…';
  } else if (isExtractionProcessing) {
    processButtonLabel = 'Processing valuation…';
  } else if (isDrafting) {
    processButtonLabel = 'Drafting email…';
  }

  let pipelineStatusColor: 'error.main' | 'secondary.main' | 'text.secondary' = 'text.secondary';
  if (isExtractionStatusError || isExtractionFailed) {
    pipelineStatusColor = 'error.main';
  } else if (isExtractionProcessing || isDrafting) {
    pipelineStatusColor = 'secondary.main';
  }

  let pipelineStatusMessage: string | null = null;
  if (isExtractionStatusError) {
    pipelineStatusMessage =
      extractionStatusError instanceof Error
        ? extractionStatusError.message
        : 'Failed to fetch valuation extraction status.';
  } else if (isExtractionFailed) {
    pipelineStatusMessage = 'Valuation extraction failed. Adjust files and try again.';
  } else if (isStartingExtraction) {
    pipelineStatusMessage = 'Starting valuation extraction…';
  } else if (isExtractionProcessing) {
    pipelineStatusMessage = '… reprocessing … estimated 5 minutes to finish processing …';
  } else if (isDrafting) {
    pipelineStatusMessage = 'Drafting final report email…';
  }

  const inputsSummary = useMemo(() => {
    const source = useS3Fallback ? 'backup bucket' : 'folder';
    const parts: string[] = [];
    parts.push(
      selectedModelName ? `${selectedModelName} model file from ${source}` : 'No model file selected'
    );
    parts.push(
      selectedReportName
        ? `${selectedReportName} report file from ${source}`
        : 'No report file selected'
    );
    if (notesParsed !== null) {
      parts.push(`${notesParsed} recent ${notesParsed === 1 ? 'note' : 'notes'} parsed`);
    }
    return parts.join(', ');
  }, [selectedModelName, selectedReportName, notesParsed, useS3Fallback]);

  return {
    selectedModelPath,
    setSelectedModelPath,
    selectedReportPath,
    setSelectedReportPath,
    selectedContactKey,
    setSelectedContactKey,
    draftEmail,
    setDraftEmail,
    handleEmailChange,
    saveDraftedEmail,
    draftDashboardUrl,
    initialPickerPrefix,
    useS3Fallback,
    modelFolderPath,
    reportFolderPath,
    modelCheckboxFiles,
    reportCheckboxFiles,
    isLoadingModelFiles,
    isLoadingReportFiles,
    contacts,
    isLoadingContacts,
    latestExtractionRun,
    dashboardOpinionUrl,
    runProcess,
    canProcess,
    processButtonLabel,
    isPipelineBusy,
    isStartingExtraction,
    isExtractionProcessing,
    isDrafting,
    isExtractionStatusError,
    isExtractionFailed,
    pipelineStatusColor,
    pipelineStatusMessage,
    inputsSummary,
    handleCopyEmail,
  };
};

export default useProcessReportModal;
